// The Connection and OAuthGrant shapes the console reads (resources.md §5.4, §6.7), the catalog of connection types
// with the credential each one takes, and the pure helpers the Connections pages share. Field names follow api/nodus/v1.
import type { KObject } from "@nodus/api";
import { Cloud, Database, Github, HardDrive, LineChart, type LucideIcon } from "lucide-react";

export interface Scope {
  org: string;
  project: string;
}

export type ConnectionType = "GitHub" | "Postgres" | "Neon" | "Supabase" | "WandB" | "S3";
export type Access = "Read" | "Write" | "ReadWrite";

export type ConnectionSpec = {
  type: ConnectionType;
  secret?: string;
  scope?: Access;
  neon?: { branch?: string };
  wandb?: { entity: string; project: string; live?: boolean };
  s3?: { bucket: string; prefix?: string; region: string; endpoint?: string };
  region?: string;
};

export type ConnectionStatus = {
  phase?: string;
  reason?: string;
  message?: string;
  conditions?: { type: string; status: "True" | "False" | "Unknown"; reason?: string; message?: string; lastTransitionTime?: string }[];
  egressHosts?: string[];
  lastVerifiedTime?: string;
  installURL?: string;
  externalId?: string;
  github?: { account: string; installationId: number; repositories?: string[] };
};

export type Connection = KObject<ConnectionSpec, ConnectionStatus>;

export type SecretStatus = { phase?: string; version?: number; keys?: string[]; versions?: { version: number; createTime: string; createdBy?: string }[] };
export type SecretObject = KObject<{ type?: string }, SecretStatus>;

/** An OAuthGrant as the identity adapter serves it: the client and scopes in spec, the rest in status. */
export type OAuthGrant = KObject<
  { clientId: string; scopes: string[]; projects?: string[] },
  { clientName?: string; user?: string; lastUsedTime?: string; expirationTime?: string }
>;

export interface TypeInfo {
  type: ConnectionType;
  label: string;
  /** What the connection is for: the purpose line of its cards. */
  purpose: string;
  icon: LucideIcon;
  /** What the connection needs from the user, named on the type grid. */
  method: string;
}

export const TYPES: readonly TypeInfo[] = [
  { type: "GitHub", label: "GitHub", purpose: "Private repositories for sandboxes and runs.", icon: Github, method: "a GitHub App install" },
  { type: "Postgres", label: "PostgreSQL", purpose: "Query imports and result sinks for any Postgres database.", icon: Database, method: "a database connection URL" },
  { type: "Neon", label: "Neon", purpose: "A Neon project's branch for imports and sinks.", icon: Database, method: "a database connection URL" },
  { type: "Supabase", label: "Supabase", purpose: "A Supabase project's database for imports and sinks.", icon: Database, method: "a database connection URL" },
  { type: "S3", label: "S3", purpose: "A bucket for run inputs and volume imports.", icon: HardDrive, method: "an IAM role or access key" },
  { type: "WandB", label: "Weights & Biases", purpose: "Live run tracking in a Weights & Biases project.", icon: LineChart, method: "an API key and project link" },
];

export function typeInfo(type: string | undefined): TypeInfo {
  return TYPES.find((t) => t.type === type) ?? { type: "GitHub", label: type ?? "Unknown", purpose: "", icon: Cloud, method: "" };
}

export function isConnectionType(v: unknown): v is ConnectionType {
  return TYPES.some((t) => t.type === v);
}

export const isDatabase = (type: ConnectionType | undefined) => type === "Postgres" || type === "Neon" || type === "Supabase";

export const ACCESS_LABEL: Record<Access, string> = { Read: "Read", Write: "Write", ReadWrite: "Read and write" };

/** The access a card states: repositories for GitHub, the scope for a database, the bucket path for S3. */
export function accessLabel(c: Connection): string {
  const s = c.spec;
  switch (s?.type) {
    case "GitHub":
      return "Selected repositories";
    case "S3":
      return s.s3 ? `s3://${s.s3.bucket}/${s.s3.prefix ?? ""}` : "Bucket";
    case "WandB":
      return s.wandb?.live ? "Live metrics" : "Run tracking";
    default:
      return ACCESS_LABEL[s?.scope ?? "Read"];
  }
}

/** The one thing a connection points at: the GitHub account, the Neon branch, the W&B project or the bucket. */
export function targetOf(c: Connection): string | undefined {
  const s = c.spec;
  switch (s?.type) {
    case "GitHub":
      return c.status?.github?.account;
    case "Neon":
      return s.neon?.branch;
    case "WandB":
      return s.wandb ? `${s.wandb.entity}/${s.wandb.project}` : undefined;
    case "S3":
      return s.s3?.bucket;
    default:
      return undefined;
  }
}

/** A link from the API is followed only when it is https: it is rendered as an href, which would run `javascript:` URLs. */
export function isHttps(url: string): boolean {
  try {
    return new URL(url).protocol === "https:";
  } catch {
    return false;
  }
}

export function connectionsHref(scope: Scope, rest = ""): string {
  return `/${encodeURIComponent(scope.org)}/${encodeURIComponent(scope.project)}/connections${rest}`;
}

export const mcpHref = (org: string) => `/${encodeURIComponent(org)}/integrations/mcp`;

export function newConnectionHref(scope: Scope, type?: ConnectionType): string {
  return connectionsHref(scope, `/new${type ? `?type=${type}` : ""}`);
}

/** The Secret keys a type's credential writes; S3 takes a role or a static key pair. */
export type CredentialMode = "role" | "key";

export function credentialKeys(type: ConnectionType, mode: CredentialMode = "role"): string[] {
  switch (type) {
    case "WandB":
      return ["WANDB_API_KEY"];
    case "S3":
      return mode === "role" ? ["ROLE_ARN"] : ["AWS_ACCESS_KEY_ID", "AWS_SECRET_ACCESS_KEY"];
    case "GitHub":
      return [];
    default:
      return ["DATABASE_URL"];
  }
}

/** Whether an existing Secret holds what the type's credential needs (either S3 form). */
export function secretFits(type: ConnectionType, keys: readonly string[] | undefined): boolean {
  const have = new Set(keys ?? []);
  const modes: CredentialMode[] = type === "S3" ? ["role", "key"] : ["role"];
  return modes.some((m) => credentialKeys(type, m).every((k) => have.has(k)));
}

const WANDB_RESERVED = new Set(["settings", "home", "login", "signup", "authorize", "site", "organizations"]);

/** Entity and project from a W&B project link (`https://wandb.ai/<entity>/<project>`); undefined when it is not one. */
export function parseWandbProject(link: string): { entity: string; project: string } | undefined {
  try {
    const u = new URL(link);
    const [entity, project] = u.pathname.split("/").filter(Boolean);
    const part = /^[A-Za-z0-9][A-Za-z0-9_.-]{0,127}$/;
    if (u.origin !== "https://wandb.ai" || u.username || u.password || !entity || !project) return undefined;
    if (WANDB_RESERVED.has(entity.toLowerCase()) || !part.test(entity) || !part.test(project)) return undefined;
    return { entity, project };
  } catch {
    return undefined;
  }
}

/** A grant's state: the server lists live grants, and one idle for 30 days is expired until its client reconnects. */
export function grantState(g: OAuthGrant, now: Date): "Active" | "Expired" {
  const end = g.status?.expirationTime;
  return end && new Date(end) <= now ? "Expired" : "Active";
}

/** The `nodus mcp install` client for a grant's client name, when the CLI configures that client. */
export function installClient(clientName: string): "claude" | "cursor" | "codex" | undefined {
  const n = clientName.toLowerCase();
  if (n.includes("claude")) return "claude";
  if (n.includes("cursor")) return "cursor";
  if (n.includes("codex")) return "codex";
  return undefined;
}
